/* eslint-disable */
import React from "react";
import { omit } from "@bonliva-crm/common";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl, { FormControlProps } from "@material-ui/core/FormControl";
import ReactSelectMui, { ReactSelectMuiProps } from "./ReactSelectMui";
import { isMulti, OptionType, Option, MultiType } from "./utils";
import { OnNewOptionClickHandler } from "react-select";

/**
 * Defining prop types for all possible variations
 */
type BaseProps<T> = Pick<
  FormControlProps,
  "error" | "required" | "onBlur" | "style" | "classes" | "fullWidth" | "className"
> & {
  autoOpen?: boolean;
  autoFocus?: boolean;
  clearable?: boolean;
  creatable?: boolean;
  disabled?: boolean;
  disableUnderline?: boolean;
  hideGroupCheckbox?: boolean;
  hideSameNameChildInGroups?: boolean;
  inputRenderer?: ReactSelectMuiProps["inputRenderer"];
  valueComponent?: ReactSelectMuiProps["valueComponent"];
  onInputKeyDown?: ReactSelectMuiProps["onInputKeyDown"];
  filterOptionsWhenTyping?: boolean;
  /**
   * What options shoul look like? (plain | checkbox)
   */
  optionType?: OptionType;
  optionRenderer?: ReactSelectMuiProps["optionRenderer"];
  onClose?: () => void;
  onFocusCustom?: (instance) => void;
  onBlurInputCustom?: (instance) => void;
  onAllSelect?: (options) => void;
  label?: string;
  /**
   * What selected values should look like in multi input? * plain | chips
   */
  multiType?: MultiType;
  name?: string;
  plain?: boolean; // don't wrap into FormControl and don't render Label
  placeholder?: string;
  renderMenu?: (menu: React.ReactElement<any>) => React.ReactElement<any>;
  sortGroups?: boolean;
  sortOptions?: ReactSelectMuiProps["sortOptions"];
  groupBy?: ReactSelectMuiProps["groupBy"];
  transformValue?: (value: Option) => any;
  renderDescriptionInValues?: boolean;
  resetValue?: ReactSelectMuiProps["resetValue"];
  wrapMenu?: (menu: React.ReactElement<any>) => React.ReactElement<any>;
  showSearchInput?: boolean;
  helperText?: string;
  skipChipDeleteIcon?: boolean;
  disablePortal?: boolean;
};

export type CreatableProps<T = string> = {
  creatable: true;
  newOptionCreator?: ReactSelectMuiProps["newOptionCreator"];
  isValidNewOption?: ReactSelectMuiProps["isValidNewOption"];
  onNewOptionClick?: OnNewOptionClickHandler<Option>;
  shouldKeyDownEventCreateNewOption?: ReactSelectMuiProps["shouldKeyDownEventCreateNewOption"];
  promptTextCreator?: ReactSelectMuiProps["promptTextCreator"];
};

type SingleBaseProps<T = string> = BaseProps<T> & {
  onChange?: (option: string) => any;
};

type MultiBaseProps<T = string> = BaseProps<T> & {
  nested?: boolean;
  onChange?: (options: string[]) => any;
  innerRef?: (ref) => void;
  /**
   * Values displayed in the input will be sorted asc
   */
  sortValues?: boolean;
};

export type SelectProps<T = string> = SingleBaseProps<T> & {
  multi: false | undefined;
  options: Option[];
  value?: T;
};

export type CreatableSelectProps<T = string> = SelectProps<T> & CreatableProps<T>;

export type MultiSelectProps<T = string> = MultiBaseProps<T> & {
  expanded?: boolean;
  multi: boolean;
  options: Option[];
  value?: T[];
};

export type CreatableMultiSelectProps<T = string> = MultiSelectProps<T> &
  CreatableProps<T> & {
    multiCreatable: true;
  };

export type AnySelectProps<T = string> =
  | SelectProps<T>
  | CreatableSelectProps<T>
  | MultiSelectProps<T>;
// | CreatableMultiSelectProps<T>; // not used anywhere

type State = {
  menuOpened: boolean;
  valueChanged: boolean;
  input: string;
};

/**
 * Wraps react-select into material-ui theme
 *
 * Either loadOptions or options must be passed in props and will define
 * the version of react-select to be used
 */
class Select extends React.Component<AnySelectProps, State> {
  static defaultProps: Partial<AnySelectProps> = {
    transformValue: (v) => (v ? v.value : v)
  };

  constructor(props) {
    super(props);
    this.state = {
      menuOpened: false,
      valueChanged: false,
      input: ""
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.value !== nextProps.value) {
      this.setState({ valueChanged: true });
    }
  }

  handleInputChange = (input) => {
    this.setState({
      input
    });
  };

  render() {
    const {
      label,
      name,
      multi,
      multiType,
      optionType,
      plain,
      value,
      onChange,
      onAllSelect,
      transformValue,
      helperText,
      filterOptionsWhenTyping = true,
      showSearchInput,
      ...rest
    } = this.props;

    const { options } = this.props;
    const sharedProps = {
      ...rest,
      onChange: (options: Option[] | Option) => {
        if (!isMulti(this.props)) {
          this.props.onChange(transformValue(options));
          return;
        }

        const groups = options
          ? options.filter((el) => !el.parent || el.hasChildren).map(transformValue)
          : [];

        const v = options
          ? options
              .filter((el) =>
                groups?.every((x) => {
                  const v = x.value
                    ? x.value.replace("||||||", "|||")
                    : x.replace("||||||", "|||");
                  return !el.parent?.includes(v);
                })
              )
              .map(transformValue)
          : [];

        this.props.onChange(v);
      },
      closeOnSelect: this.props.optionType !== "checkbox",
      disabled: this.props.disabled,
      fullWidth: this.props.fullWidth,
      id: name,
      hasMuiLabel: Boolean(label),
      multi,
      multiType,
      onClose: () => {
        this.setState({ menuOpened: false });
        this.props.onClose && this.props.onClose();
      },
      onOpen: () => this.setState({ menuOpened: true }),
      optionType,
      onAllSelect,
      removeSelected: this.props.optionType !== "checkbox",
      renderMenu: this.props.renderMenu,
      value,
      filterOptionsWhenTyping,
      showSearchInput,
      options
    };

    const control = (
      <ReactSelectMui
        {...sharedProps}
        inputProps={{ autoComplete: "off" }}
        onInputChange={(e) => this.handleInputChange(e)}
      />
    );

    const menuOpen = this.state.menuOpened ? true : undefined;
    const hasValue = value != null && value !== "";
    const shrinkLabel = isMulti(this.props)
      ? hasValue && (value?.length > 0 || menuOpen)
      : hasValue || menuOpen;
    const shrinkLabel2 = this.state.input.length > 0 ? true : false;

    const omitPropsInFormControl: string[] = [
      "autoFocus",
      "autoOpen",
      "clearable",
      "creatable",
      "disableUnderline",
      "exclusiveOptions",
      "groupBy",
      "groupByField",
      "groupOptions",
      "hideGroupCheckbox",
      "hideSameNameChildInGroups",
      "innerRef",
      "inputRenderer",
      "valueComponent",
      "onInputKeyDown",
      "isValidNewOption",
      "loadOptions",
      "nested",
      "newOptionCreator",
      "onBlurInputCustom",
      "onChange",
      "onClose",
      "onFocusCustom",
      "onNewOptionClick",
      "optionRenderer",
      "options",
      "plain",
      "promptTextCreator",
      "renderDescriptionInValues",
      "renderMenu",
      "resetValue",
      "shouldKeyDownEventCreateNewOption",
      "sortGroups",
      "sortOptions",
      "sortValues",
      "wrapMenu"
    ];

    const formControlProps = omit(rest, ...(omitPropsInFormControl as any));

    if (plain) {
      return control;
    }

    return (
      <FormControl {...formControlProps} data-test="Select" data-test-id={name}>
        {label && (
          <InputLabel
            className="Select_label"
            htmlFor={name}
            shrink={shrinkLabel || shrinkLabel2}
          >
            {label}
          </InputLabel>
        )}
        {control}
        {helperText && <FormHelperText error>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
}

export default Select;
